import React from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ControlledInput } from 'src/components/ui/Input/Input';
import { ControlledRadioButtonControl } from 'src/components/ui/RadioButtonControl/RadioButtonControl';
import Button, { ButtonToneProps } from 'src/components/ui/Button/Button';
import { toast } from 'react-toastify';
import { $publicApi } from 'src/services/api';
import logger from 'src/utils/logger';
import { extractBackendErrorMessage } from '../errorUtils';
import { BankAndTermsFormValues, bankAndTermsSchema } from '../schema';
import { useRegistrationFormContext } from '../RegistrationFormContext';
import TermsList from '../TermsList';
import RegistrationFormErrors from '../RegistrationFormErrors';

interface BankAndTermsStepProps {
  registrationKey: string;
  onAdvance: () => void;
  onBack: () => void;
}

const BankAndTermsStep: React.FC<BankAndTermsStepProps> = ({ registrationKey, onAdvance, onBack }) => {
  const { t } = useTranslation();
  const { formState, setFormState } = useRegistrationFormContext();

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<BankAndTermsFormValues>({
    resolver: zodResolver(bankAndTermsSchema),
    defaultValues: formState.bankAndTerms,
    mode: 'onSubmit',
  });

  const paymentTerms = watch('payment_terms');
  const acceptedTermIds = watch('accepted_term_ids');

  const termsQuery = $publicApi.useQuery('get', '/registrations/{registration_key}/terms/', {
    params: { path: { registration_key: registrationKey } },
  });
  const terms = termsQuery.data ?? [];

  const { mutateAsync, isPending } = $publicApi.useMutation('put', '/registrations/{registration_key}');

  const toggleTerm = (termId: number, accepted: boolean) => {
    const next = accepted
      ? Array.from(new Set([...acceptedTermIds, termId]))
      : acceptedTermIds.filter(id => id !== termId);
    setValue('accepted_term_ids', next, { shouldValidate: false });
  };

  const allTermsAccepted =
    terms.length > 0 && terms.every(term => term.id != null && acceptedTermIds.includes(term.id));

  const onSubmit = async (values: BankAndTermsFormValues) => {
    if (!allTermsAccepted) return;
    try {
      await mutateAsync({
        params: { path: { registration_key: registrationKey } },
        body: {
          payment_terms: values.payment_terms,
          bank_account: values.bank_account,
        },
      });
      const acceptedTerms = terms.filter(term => term.id != null && values.accepted_term_ids.includes(term.id));
      setFormState(prev => ({
        ...prev,
        bankAndTerms: values,
        acceptedTerms,
      }));
      onAdvance();
    } catch (err) {
      logger.error('Failed to save bank/terms data', err);
      const backendMessage = extractBackendErrorMessage(err);
      toast.error(backendMessage || t('registration.errors.save_failed'));
    }
  };

  const paymentTermsOptions = [
    { label: t('registration.payment_terms.invoice'), value: 'invoice' },
    { label: t('registration.payment_terms.debit'), value: 'debit' },
  ];

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-24">
      <h3 className="typo-title-24 text-full">{t('registration.steps.bank_and_terms')}</h3>

      <RegistrationFormErrors errors={errors} />

      <div>
        <h4 className="typo-title-20 text-full mb-12">{t('registration.fields.payment_terms')}</h4>
        <ControlledRadioButtonControl
          control={control}
          name="payment_terms"
          options={paymentTermsOptions}
          defaultValue={formState.bankAndTerms.payment_terms}
        />
      </div>

      {paymentTerms === 'debit' && (
        <div>
          <h4 className="typo-title-20 text-full mb-12">{t('registration.fields.bank_account')}</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
            <ControlledInput
              control={control}
              name="bank_account.bank_name"
              label={t('registration.fields.bank_name')}
              placeholder=""
              className="col-span-1 md:col-span-2"
            />
            <ControlledInput
              control={control}
              name="bank_account.iban"
              label={t('registration.fields.iban')}
              placeholder={t('registration.placeholders.iban')}
            />
            <ControlledInput
              control={control}
              name="bank_account.bic"
              label={t('registration.fields.bic')}
              placeholder={t('registration.placeholders.bic')}
            />
          </div>
        </div>
      )}

      <div>
        <h4 className="typo-title-20 text-full mb-12">{t('registration.terms.title')}</h4>
        <p className="typo-text-14 text-faded mb-12">{t('registration.terms.copy')}</p>
        <TermsList
          registrationKey={registrationKey}
          terms={terms}
          isLoading={termsQuery.isLoading}
          acceptedTermIds={acceptedTermIds}
          onToggle={toggleTerm}
        />
        {!allTermsAccepted && terms.length > 0 && (
          <p className="typo-text-14 status-error-full mt-8">{t('registration.terms.must_accept_all')}</p>
        )}
      </div>

      <div className="flex justify-between pt-16">
        <Button title={t('registration.back')} tone={ButtonToneProps.SECONDARY} onClick={onBack} />
        <Button
          title={t('registration.next')}
          tone={ButtonToneProps.PRIMARY_BLUE}
          type="submit"
          disabled={isPending || !allTermsAccepted}
        />
      </div>
    </form>
  );
};

export default BankAndTermsStep;
